// import components
import { Button, Menu, MenuItem, SxProps } from '@mui/material';

// import enums
import { ViewContext, View } from '../../enums';

// import hooks
import { useEffect, useState } from 'react';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import usePlanningEntity, {
  PlanningEntityTypePreference
} from '../../hooks/usePlanningEntity';

// import types
import { PlanningEntityType } from 'generated';

// import utils
import { getViewContext } from '../../utils';
import { Icon } from '@sdb/planning-ui';

interface Props {
  view: View;
  disabled?: boolean;
  sx?: SxProps;
}

export default function EntityMenu({ view, disabled, sx }: Props) {
  const {
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasActivityPlanningViewAccess,
    hasKidsPlanAccess,
    hasProjectTeamAccess
  } = useSelector(({ grants }) => grants);
  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.planningEntityType'
  });
  const [entity, setEntity] = usePlanningEntity();
  const selectedEntity = entity[view];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  let planningEntityTypes: PlanningEntityType[] = [];
  const context = getViewContext();
  if (
    (hasMyActivitiesProjectAccess && context === ViewContext.Own) ||
    ((hasManageOwnProjectsAccess || hasProjectTeamAccess) &&
      context === ViewContext.Team &&
      view != View.Timeline)
  ) {
    planningEntityTypes.push('PROJECT');
  }
  if (hasWorkPlacePlanningViewAccess && view != View.Timeline) {
    planningEntityTypes.push('WORKPLACE');
  }
  if (hasActivityPlanningViewAccess && view == View.Timeline) {
    planningEntityTypes.push('ROUTE');
  }
  if (
    hasActivityPlanningViewAccess ||
    hasKidsPlanAccess ||
    (hasProjectTeamAccess && view === View.List)
  ) {
    planningEntityTypes.push('EMPLOYEE');
  }
  if (hasActivityPlanningViewAccess && view != View.Timeline) {
    planningEntityTypes.push('CLIENT');
  }

  // Only Roster and List view when viewing My Activities
  if (context === ViewContext.Own) {
    planningEntityTypes = planningEntityTypes.filter(
      (t: PlanningEntityType) => t === 'WORKPLACE' || t === 'PROJECT'
    );
  }

  useEffect(() => {
    if (!planningEntityTypes.includes(selectedEntity)) {
      setEntity({
        [view]: planningEntityTypes[0]
      } as PlanningEntityTypePreference);
    }
  }, [view, planningEntityTypes]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={disabled}
        sx={{
          display: 'flex',
          gap: (theme) => theme.spacing(2),
          zIndex: 31,
          overflow: 'hidden',
          ...(sx || {})
        }}
      >
        <Icon type="sliders" fontSize="small" />
        {t(selectedEntity)}
        {!disabled && <Icon type="chevron-down" fontSize="small" />}
      </Button>
      {!disabled && (
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {planningEntityTypes.map((entity: PlanningEntityType, i: number) => (
            <MenuItem
              key={i}
              onClick={() => {
                setEntity({ [view]: entity } as PlanningEntityTypePreference);
                handleClose();
              }}
            >
              {t(entity)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
